import * as Yup from "yup";

export interface SelectOption {
  value: string;
  label: string;
}

export const statusOptions: SelectOption[] = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
  { label: "Repair", value: "REPAIR" },
  { label: "Rental", value: "RENTAL" },
  { label: "Out Of Service", value: "OUT OF SERVICE" }
];

/** Portable IT asset types (tablet, iPad, scan gun, laptop) — hide fleet/vehicle fields */
export const EQUIPMENT_TYPES_WITHOUT_VEHICLE_IDS = ["IPAD", "TABLET", "LAPTOP", "SCANGUN"] as const;

export const isEquipmentTypeWithoutVehicleIds = (equipmentType?: string | null): boolean => {
  if (!equipmentType?.trim()) {
    return false;
  }


  const normalized = equipmentType.trim().toUpperCase().replace(/[\s_-]+/g, "");
  return EQUIPMENT_TYPES_WITHOUT_VEHICLE_IDS.some(
    (type) => type === normalized || normalized.includes(type)
  );
};

export const buildEquipmentSubmitPayload = (
  values: Record<string, unknown>,
  options: { isEdit: boolean; location?: string }
): Partial<EquipmentFormModel> => {
  const payload = { ...values } as Record<string, unknown>;
  const isPortableAsset = isEquipmentTypeWithoutVehicleIds(
    payload.equipmentType as string | undefined
  );

  delete payload.utilizationPercent;

  if (isPortableAsset) {
    delete payload.vin;
    delete payload.licensePlate;
    delete payload.dotInspectionDate;
    delete payload.dotCertificateUrl;
  }

  if (!options.isEdit) {
    payload.location = options.location || payload.location;
    delete payload.id;
    delete payload.registrationDocUrl;
    delete payload.dotCertificateUrl;
  }

  return payload as Partial<EquipmentFormModel>;
};

export interface EquipmentDetailModel {
  id: string | null;
  unitNumber: string | null;
  equipmentType: string | null;
  assetDescriptor: string | null;
  make: string | null;
  model: string | null;
  year: string | null;
  vin: string | null;
  licensePlate: string | null;
  registrationDate: string | null;
  dotInspectionDate: string | null;
  domicileTerminal: string | null;
  location: string | null;
  garageCity: string | null;
  garageState: string | null;
  status: string | null;
  disposition: string | null;
  utilizationPercent: string | null;
  dwellTimeDays: string | null;
  leaseType: string | null;
  vendor: string | null;
  productLine: string | null;
  termMonths: string | null;
  annualAllowance: string | null;
  annualRateMeasurement: string | null;
  monthlyRate: string | null;
  size: string | null;
  accessory: string | null;
  serialNumber: string | null;
  inServiceDate: string | null;
  expirationDate: string | null;
  fuelType: string | null;
  registrationDocUrl: string | null;
  dotCertificateUrl: string | null;
  notes: string | null;
  assetTag: string | null;
  employeeAssigned: string | null;
  assignedDate: string | null;
  purchaseDate: string | null;
  lastCheckIn: string | null;
}
export interface EquipmentFormModel {
  id: string | null;
  unitNumber: string | null;
  equipmentType: string | null;
  assetDescriptor: string | null;
  make: string | null;
  model: string | null;
  year: string | null;
  vin: string | null;
  licensePlate: string | null;
  registrationDate: string | null;
  dotInspectionDate: string | null;
  domicileTerminal: string | null;
  location: string | null;
  garageCity: string | null;
  garageState: string | null;
  status: string | null;
  disposition: string | null;
  utilizationPercent: string | null;
  dwellTimeDays: string | null;
  leaseType: string | null;
  vendor: string | null;
  productLine: string | null;
  termMonths: string | null;
  annualAllowance: string | null;
  annualRateMeasurement: string | null;
  monthlyRate: string | null;
  size: string | null;
  accessory: string | null;
  serialNumber: string | null;
  inServiceDate: string | null;
  expirationDate: string | null;
  fuelType: string | null;
  registrationDocUrl: string | null;
  dotCertificateUrl: string | null;
  notes: string | null;
  assetTag: string | null;
  employeeAssigned: string | null;
  assignedDate: string | null;
  purchaseDate: string | null;
  lastCheckIn: string | null;
}

/** List row shape returned by GET /equipments */
export type EquipmentListModel = EquipmentFormModel;

export type EquipmentActivityLogAction = "CREATE" | "UPDATE" | "DEACTIVATE";

/** Cross-equipment activity log row from GET /equipments/activity-log/all */
export interface EquipmentActivityLogModel {
  changedAt: string;
  modifiedBy: string;
  action: EquipmentActivityLogAction | string;
  changeDescription: string;
  equipmentId: string;
  unitNumber: string | null;
  equipmentType: string | null;
  location: string | null;
}

/** Per-equipment change log row from GET /equipments/activity-log/{equipmentId} */
export interface EquipmentLogItem {
  changedAt: string;
  modifiedBy: string;
  action: EquipmentActivityLogAction | string;
  changeDescription: string;
}

export interface EquipmentLogFilterModel {
  dateFrom?: string;
  dateTo?: string;
}

export const EquipmentLogFilterSchema: Yup.ObjectSchema<EquipmentLogFilterModel> = Yup.object({
  dateFrom: Yup.string(),
  dateTo: Yup.string(),
}).defined();

export const EQUIPMENT_ACTIVITY_LOG_ACTION_OPTIONS = [
  { id: "CREATE", name: "Create" },
  { id: "UPDATE", name: "Update" },
  { id: "DEACTIVATE", name: "Deactivate" },
];

export const getEquipmentInfoValidation = (): Yup.ObjectSchema<EquipmentFormModel> => {

  return Yup.object().shape({
    unitNumber: Yup.string().required("Unit number is required"),
    equipmentType: Yup.string().required("Type is required"),
    assetDescriptor: Yup.string().nullable(),
    make: Yup.string().nullable(),
    model: Yup.string().nullable(),
    year: Yup.string()
      .nullable()
      .test(
        "year-format",
        "Enter a valid 4-digit year",
        (value) => !value || /^(19|20)\d{2}$/.test(value)
      ),
    vin: Yup.string()
      .nullable()
      .when("equipmentType", {
        is: (type: string) => isEquipmentTypeWithoutVehicleIds(type),
        then: Yup.string().nullable().notRequired(),
        otherwise: Yup.string().nullable(),
      }),

    licensePlate: Yup.string()
      .nullable()
      .max(200, "Too long")
      .when("equipmentType", {
        is: (type: string) => isEquipmentTypeWithoutVehicleIds(type),
        then: Yup.string().nullable().max(200, "Too long").notRequired(),
        otherwise: Yup.string().nullable().max(200, "Too long"),
      }),
    registrationDate: Yup.string().nullable(),
    dotInspectionDate: Yup.string().nullable(),

    location: Yup.string().required("Terminal is required"),
    status: Yup.string().nullable(),

    leaseType: Yup.string().nullable(),
    vendor: Yup.string().nullable(),

    notes: Yup.string().nullable().max(1000, "Notes cannot exceed 1000 characters"),
    assetTag: Yup.string().nullable().max(200, "Too long"),
    employeeAssigned: Yup.string().nullable().max(200, "Too long"),
    assignedDate: Yup.string().nullable(),
    purchaseDate: Yup.string().nullable(),
    lastCheckIn: Yup.string().nullable(),

  }).defined();
};